<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import PremiumRequired from '@/components/PremiumRequired.vue'
import ResearchCitationPanels from '@/components/ResearchCitationPanels.vue'
import StrategyBanner from './details/StrategyBanner.vue'
import StrategyBreadcrumbs from './details/StrategyBreadcrumbs.vue'
import StrategyPanels from './details/StrategyPanels.vue'
import StrategyRelated from './details/StrategyRelated.vue'
import StrategyResourceList from './details/StrategyResourceList.vue'
import StrategySaveToLibraryPanel from './details/StrategySaveToLibraryPanel.vue'
import StrategyCollectionBreadcrumb from './StrategyCollectionBreadcrumb.vue'
import { getStrategy } from './api'
import { strategyPageSections } from './strategy_page_sections'
import { getLibraryGoal } from '@/library/api'
import { getAllCuratedCollections } from '@/curated_collections/api'
import { useUserStore } from '@/authentication/user.store'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { setTitle } from '@/util/title'
import { VUE2_CONTAINER_WIDTHS } from '@/util/vue2_layout'
import type { StrategyDetail } from './strategy_types'
import type { CuratedCollection } from '@/curated_collections/curated_collection_types'

const props = withDefaults(defineProps<{
  slugOrUuid: string
  libraryGoalUuid?: string
}>(), {
  libraryGoalUuid: ''
})

const { isPremium } = storeToRefs(useUserStore())
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()

const strategy = ref<StrategyDetail | null>(null)
const verifiedLibraryGoal = ref(false)
const collections = ref<Pick<CuratedCollection, 'uuid' | 'name'>[]>([])

const analytics = getAnalyticsTracker().getChildContext({
  strategy_uuid: computed(() => strategy.value?.uuid),
  strategy_title: computed(() => strategy.value?.title),
  research_citations_count: computed(() => strategy.value?.research_citations?.length ?? 0)
})

const sections = computed(() => strategy.value && strategyPageSections(strategy.value, verifiedLibraryGoal.value))
const showPremiumBlock = computed(() => Boolean(strategy.value && !isPremium.value && !strategy.value.demo))

async function isVerifiedLibraryGoal() {
  if (!props.libraryGoalUuid) {
    return false
  }
  return getLibraryGoal(props.libraryGoalUuid).then(() => true, () => false)
}

async function fetchCollectionsContaining(strategyUuid: string) {
  try {
    const curatedCollections = await getAllCuratedCollections({ content: 'true' })
    if (strategy.value?.uuid !== strategyUuid) return
    collections.value = curatedCollections
      .filter((collection) => collection.content?.some((content) => content.content_uuid === strategyUuid))
      .map(({ uuid, name }) => ({ uuid, name }))
  } catch (error) {
    console.error(error)
  }
}

function showStrategy(loaded: StrategyDetail) {
  strategy.value = loaded
  setTitle(`${loaded.title} | UDL Strategies`)
  updateFeedbackContext({ uuid: loaded.uuid, title: loaded.title })
  fetchCollectionsContaining(loaded.uuid)
}

function sendViewedEvent() {
  analytics.sendEvent(
    EVENT_NAMES.STRATEGY_DETAIL.VIEWED_PAGE,
    {
      uuid: strategy.value?.uuid,
      title: strategy.value?.title,
      udl_checkpoints: strategy.value?.udl_standards_refs
    },
    {
      cu: strategy.value?.uuid,
      ct: strategy.value?.title
    }
  )
}

let latestFetch = 0

async function fetchContent() {
  const thisFetch = ++latestFetch
  strategy.value = null
  collections.value = []
  try {
    const [loaded, verified] = await Promise.all([getStrategy(props.slugOrUuid), isVerifiedLibraryGoal()])
    if (thisFetch !== latestFetch) return
    verifiedLibraryGoal.value = verified
    if ('status' in loaded) {
      console.error(loaded.message)
    } else {
      showStrategy(loaded)
    }
    sendViewedEvent()
  } catch (error) {
    console.error(error)
  }
}

watch(() => [props.slugOrUuid, props.libraryGoalUuid], fetchContent, { immediate: true })
</script>

<template>
  <div class="container pt-5" :class="VUE2_CONTAINER_WIDTHS">
    <CopyTrackWrapper
      v-if="strategy && sections"
      type="strategy"
      :title="strategy.title"
      :uuid="strategy.uuid"
      gate-copy
      :demo="strategy.demo"
    >
      <PremiumRequired v-if="showPremiumBlock" />
      <template v-else>
        <StrategyBreadcrumbs :title="strategy.title" :tags="strategy.tags" />
        <CopyTrackWrapper type="strategy_banner" :title="strategy.title" :uuid="strategy.uuid" gate-copy :demo="strategy.demo">
          <StrategyBanner v-bind="sections.banner" />
        </CopyTrackWrapper>
        <div class="md:grid md:grid-cols-12 md:gap-x-[30px]">
          <div class="md:col-span-8">
            <StrategyResourceList v-bind="sections.resources" />
            <StrategyPanels v-bind="sections.implementation" />
            <StrategyPanels v-bind="sections.examples" />
            <ResearchCitationPanels v-if="sections.researchCitations" :research-citations="sections.researchCitations" />
          </div>
          <div class="md:col-span-4">
            <StrategySaveToLibraryPanel class="mt-12" :strategy-uuid="strategy.uuid" :strategy-title="strategy.title" />
            <StrategyCollectionBreadcrumb :collections="collections" />
          </div>
        </div>
        <StrategyRelated v-if="sections.related" v-bind="sections.related" />
      </template>
    </CopyTrackWrapper>
  </div>
</template>
